import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import NoteLinkPatient from './NoteLinkPatient';
import NoteAddPatient from './NoteAddPatient';
import NoteUpdatePatient from './NoteUpdatePatient';
import NoteViewPatient from './NoteViewPatient';
import fetchPatient from '../../utils/patient/fetchPatient';

const NotePatient = ({ patient: passedPatient, note, source, onLinkPatient }) => {
  const { t } = useTranslation();
  const [patient, setPatient] = useState(passedPatient);

  useEffect(() => {
    setPatient(passedPatient);
  }, [passedPatient]);

  const updatePatientState = async (patientUuid) => {
    const updatedPatient = await fetchPatient(patientUuid);
    setPatient(updatedPatient);
  };

  const getPatientFields = () => [
    {
      key: 'full_name',
      label: t('patient_form.fields.full_name_short'),
      getValue: patient => patient?.getFullName() || ''
    },
    {
      key: 'gender',
      label: t('patient_form.fields.gender'),
      getValue: patient => patient?.getGenderSubject() || ''
    },
    {
      key: 'dob',
      label: t('patient_form.fields.dob'),
      getValue: patient => patient?.getDOB() || ''
    },
    {
      key: 'age',
      label: t('patient_form.fields.age'),
      getValue: patient => patient?.getAge() || ''
    },
    {
      key: 'location',
      label: t('patient_form.fields.location'),
      getValue: patient => patient?.getLocation() || ''
    },
    {
      key: 'civil_status',
      label: t('patient_form.fields.civil_status'),
      getValue: patient => patient?.getCivilStatus() || ''
    },
    {
      key: 'occupation',
      label: t('patient_form.fields.occupation'),
      getValue: patient => patient?.getOccupation() || ''
    },
    {
      key: 'religion',
      label: t('patient_form.fields.religion'),
      getValue: patient => patient?.getReligion() || ''
    },
    {
      key: 'weight',
      label: t('patient_form.fields.weight'),
      getValue: patient => patient?.getWeight() || ''
    },
    {
      key: 'height',
      label: t('patient_form.fields.height'),
      getValue: patient => patient?.getHeight() || ''
    },
    {
      key: 'blood_type',
      label: t('patient_form.fields.blood_type'),
      getValue: patient => patient?.getBloodType() || ''
    },
    {
      key: 'bmi',
      label: t('patient_form.fields.bmi'),
      getValue: patient => patient?.getBMI() || ''
    }
  ];

  // Returns a list of fields that have updates and are blank in the patient
  const getFilteredPatientUpdates = () => {
    if (!note?.hasPatientUpdates()) return null;

    const patientUpdates = note.getPatientUpdates();
    if (!patientUpdates) return null;

    // Get only the fields that are displayed (blank fields with updates)
    const fieldsToUpdate = getPatientFields().filter(field => {
      const hasUpdate = Boolean(patientUpdates[field.key]);
      const currentValue = patient ? field.getValue(patient) : '';
      const isCurrentValueEmpty = !currentValue || currentValue.trim() === '';
      return hasUpdate && isCurrentValueEmpty;
    });

    // Create an object with only the fields to update, including their labels
    return fieldsToUpdate.reduce((acc, field) => {
      acc[field.key] = {
        value: patientUpdates[field.key],
        label: field.label
      };
      return acc;
    }, {});
  };

  const filteredUpdates = getFilteredPatientUpdates();
  const hasUpdates = filteredUpdates && Object.keys(filteredUpdates).length > 0;

  // If there's no patient but there are updates, show NoteAddPatient
  if (!patient && hasUpdates) {
    console.log('Rendering NoteAddPatient - no patient but has updates');
    const fields = getPatientFields().map(field => ({
      key: field.key,
      label: field.label
    }));
    return <NoteAddPatient 
      patientUpdates={filteredUpdates} 
      fields={fields}
      source={source}
      onLinkPatient={onLinkPatient} 
      onPatientCreated={updatePatientState}
    />;
  }

  // If there's no patient and no updates, show NoteLinkPatient
  if (!patient) {
    console.log('Rendering NoteLinkPatient - no patient and no updates');
    return <NoteLinkPatient onLinkPatient={onLinkPatient} />;
  }

  // If there's a patient and updates, and updates haven't been skipped, show NoteUpdatePatient
  if (hasUpdates) {
    console.log('Rendering NoteUpdatePatient - has both patient and updates');
    return (
      <NoteUpdatePatient 
        patient={patient} 
        patientUpdates={filteredUpdates}
        onUpdate={() => updatePatientState(patient.getUUID())}
      />
    );
  }

  // If there's just a patient, show NoteViewPatient
  console.log('Rendering NoteViewPatient - has patient, no updates');
  return <NoteViewPatient patient={patient} />;
};

export default NotePatient;
